import type { SupabaseClient } from '@supabase/supabase-js';
import type { SupportedAIApp } from '../types';
import { recordJobProvenance } from '../provenance/record';
import type { AIProvenanceFlags } from '../types';

export type PersistProvenanceArgs = {
  supabase: SupabaseClient;
  target: 'generations' | 'images' | 'imagemotion_project_images' | 'imagemotion_projects';
  id: string;
  app: SupportedAIApp;
  operation: string;
  sourceAssetId?: string;
  outputAssetId?: string;
  provider?: string;
  model?: string;
  /** Spoof attempt — ignored */
  clientClassification?: unknown;
};

/**
 * Build trusted provenance and persist via SECURITY DEFINER RPC.
 * Safe to call from app clients after job completion.
 */
export async function persistAiProvenance(
  args: PersistProvenanceArgs,
): Promise<{ flags: AIProvenanceFlags; ok: boolean }> {
  const { flags, metadata } = await recordJobProvenance({
    app: args.app,
    operation: args.operation,
    sourceAssetId: args.sourceAssetId,
    outputAssetId: args.outputAssetId,
    provider: args.provider,
    model: args.model,
    clientClassification: args.clientClassification,
  });

  try {
    const { error } = await args.supabase.rpc('apply_ai_provenance', {
      p_target: args.target,
      p_id: args.id,
      p_ai_generated: flags.ai_generated,
      p_ai_modified: flags.ai_modified,
      p_ai_assisted: flags.ai_assisted,
      p_ai_classification: flags.ai_classification,
      p_ai_provider: flags.ai_provider,
      p_ai_model: flags.ai_model,
      p_ai_operation: flags.ai_operation,
      p_source_asset_id: flags.source_asset_id,
      p_output_asset_id: flags.output_asset_id,
      p_ai_created_at: flags.ai_created_at,
      p_provenance_version: flags.provenance_version,
      p_visible_disclosure_applied: flags.visible_disclosure_applied,
      p_machine_readable_provenance_applied: flags.machine_readable_provenance_applied,
      p_provenance_health: flags.provenance_health,
      p_provenance_failure_reason: flags.provenance_failure_reason,
      p_source_app: args.app,
      p_metadata_patch: args.target === 'generations' ? metadata : null,
    });

    if (error) {
      console.error('apply_ai_provenance failed:', error.message);
      return { flags, ok: false };
    }
    return { flags, ok: true };
  } catch (err) {
    console.error('persistAiProvenance error:', err);
    return { flags, ok: false };
  }
}
